// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';
import { log, mountEventDrawer, logAsText, ingest, type LogEvent } from './eventlog';
import { closeDrawer, escapeHtml, glossaryTerm, icon, openModal, registerShell, toast, toggleDrawer, wireGlossary } from './ui';
import { MODELS, LOCAL_ENCODINGS, DEFAULT_MODEL_ID, findModel, findLocalEncoding } from './data/models';
import { parseConversation, ParseError, unusualRoles } from './parse';
import { budgetCsv, copyText, saveText, trimmedJson, type BudgetColumn } from './artefacts';
import { planPack, applyPlan, validateConversation, type PackStrategy } from './packer';
import { compareModels, diagnose, diverge } from './diff';
import { bytes, commas, compactTokens, percent, preview } from './format';
import { rollup as rollupSegments, type Rollup } from './segments';
import type { Conversation, Segment, ToolDefinition } from './types';
import type { TemplateCapabilities } from './render';
import type { LossMask } from './lossmask';

/* ── State ─────────────────────────────────────────────────────── */

interface ModelResult {
  modelId: string;
  label: string;
  prompt: string;
  ids: number[];
  tokens: string[];
  segments: Segment[];
  toolSpans: Array<{ name: string; start: number; end: number; tokens: number; parentIndex: number }>;
  cuts: number[];
  total: number;
  skipped: number;
  degenerate: boolean;
  approximateBoundaries: boolean;
  rollup: Rollup;
  contextWindow: number | null;
  tokenizerBytes: number;
  templateName: string;
  availableTemplates: string[];
  template: string;
  capabilities: TemplateCapabilities;
  loss: LossMask;
  estimate: boolean;
}

interface FailedResult {
  modelId: string;
  label: string;
  error: string;
  kind: string;
  template?: string;
}

const state = {
  conversation: null as Conversation | null,
  rawInput: '',
  /** The thing the cache view diffs against. Tagged with the model it came
   *  from: token ids only mean anything within one tokenizer, so a comparison
   *  captured under one model must never be diffed against another. */
  previousPrompt: null as { label: string; modelId: string; ids: number[]; tokens: string[] } | null,
  selected: [DEFAULT_MODEL_ID] as string[],
  results: new Map<string, ModelResult>(),
  failures: new Map<string, FailedResult>(),
  busy: new Set<string>(),
  addGenerationPrompt: true,
  budget: 0,
  strategy: 'oldest-first' as PackStrategy,
  view: 'prompt' as 'prompt' | 'budget' | 'compare' | 'cache' | 'training',
};

let worker: Worker | null = null;
let jobCounter = 0;
const jobs = new Map<number, { modelId: string; purpose: 'primary' | 'compare'; label?: string }>();

/* ── Boot ──────────────────────────────────────────────────────── */

const app = document.getElementById('app')!;
app.innerHTML = shell();

const els = {
  input: document.getElementById('input') as HTMLTextAreaElement,
  inputStatus: document.getElementById('input-status')!,
  modelChips: document.getElementById('model-chips')!,
  modelAdd: document.getElementById('model-add') as HTMLSelectElement,
  modelNote: document.getElementById('model-note')!,
  results: document.getElementById('results')!,
  tabs: document.getElementById('tabs')!,
  view: document.getElementById('view')!,
  drawer: document.getElementById('drawer')!,
  drawerBody: document.getElementById('drawer-body')!,
  modalHost: document.getElementById('modal-host')!,
  toasts: document.getElementById('toasts')!,
  dropzone: document.getElementById('dropzone')!,
};

registerShell({ modalHost: els.modalHost, drawer: els.drawer, toastHost: els.toasts });
mountEventDrawer(els.drawerBody, closeDrawer);
wireGlossary(app);
log.system('chatprint ready. Nothing you paste leaves this tab.', 'ok');

initWorker();
renderModelPicker();
wireEvents();
renderResults();

/* ── Shell markup ──────────────────────────────────────────────── */

function shell(): string {
  return `
  <header class="topbar">
    <div class="brand">
      <span class="brand-mark">${brandMark()}</span>
      <span class="brand-text">
        <span class="brand-name">chatprint</span>
        <span class="brand-tag">prompt inspector</span>
      </span>
    </div>
    <nav class="topnav">
      <button type="button" class="nav-btn" id="nav-how">How it works</button>
      <button type="button" class="nav-btn" id="nav-privacy">Privacy</button>
      <button type="button" class="nav-btn" id="nav-about">About</button>
      <button type="button" class="nav-btn" id="nav-log">Event log</button>
    </nav>
  </header>

  <main class="main-content">
    <section class="hero">
      <h1>“This model's maximum context length is 128000 tokens.”<br /><span>Here is exactly what ate it.</span></h1>
      <p class="lede">
        Paste the request your code was about to send. chatprint renders it through the model's own
        ${glossaryTerm('chat template')}, counts the ${glossaryTerm('token', 'tokens')} exactly, and shows you which part —
        system prompt, ${glossaryTerm('tool definition', 'tool definitions')}, retrieved documents, history — is actually
        spending your ${glossaryTerm('context window')}. Then it trims it to fit.
      </p>
      <p class="trust" id="trust-badge">
        ${icon('shield')} Runs entirely in your browser. Your prompt is never uploaded — the only request this
        page makes is an anonymous download of a public tokenizer file.
      </p>
    </section>

    <section class="panel" id="input-panel">
      <div class="panel-head">
        <h2><span class="step">1</span> Your request</h2>
        <div class="panel-actions">
          <button type="button" class="pill primary" id="btn-sample">Try a sample</button>
          <button type="button" class="pill" id="btn-paste">Paste</button>
          <button type="button" class="pill" id="btn-clear">Clear</button>
        </div>
      </div>
      <div class="dropzone" id="dropzone">
        <textarea id="input" spellcheck="false" autocomplete="off" aria-label="Request body JSON"
          placeholder='Paste an OpenAI or Anthropic request body, or a bare messages array:

{
  "messages": [
    { "role": "system", "content": "You are a helpful assistant." },
    { "role": "user", "content": "Hello" }
  ],
  "tools": [ ... ]
}

Or drop a .json file here.'></textarea>
      </div>
      <p class="status" id="input-status"></p>
    </section>

    <section class="panel" id="model-panel">
      <div class="panel-head">
        <h2><span class="step">2</span> Models</h2>
        <div class="panel-actions">
          <select id="model-add" aria-label="Add a model"></select>
        </div>
      </div>
      <div class="chips" id="model-chips"></div>
      <p class="status" id="model-note"></p>
    </section>

    <section class="results" id="results">
      <nav class="tabs" id="tabs" role="tablist"></nav>
      <div class="view" id="view"></div>
    </section>
  </main>

  <footer class="site-footer">
    <div class="footer-inner">
      Built by <a href="https://benrichardson.dev/" target="_blank" rel="noopener">benrichardson.dev</a>
      · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>
    </div>
  </footer>

  <aside class="drawer" id="drawer" hidden aria-label="Event log">
    <div id="drawer-body"></div>
  </aside>
  <div class="modal-host" id="modal-host" hidden></div>
  <div class="toasts" id="toasts" aria-live="polite"></div>
  `;
}

function brandMark(): string {
  return `<svg viewBox="0 0 64 64" aria-hidden="true">
    <rect x="10.5" y="12.5" width="43" height="39" rx="5" fill="none" stroke="currentColor" stroke-width="3" opacity=".45"/>
    <rect x="17" y="20" width="30" height="5" rx="2.5" fill="var(--seg-system)"/>
    <rect x="17" y="29" width="22" height="5" rx="2.5" fill="var(--seg-tools)"/>
    <rect x="17" y="38" width="26" height="5" rx="2.5" fill="var(--seg-document)"/>
    <rect x="17" y="47" width="14" height="5" rx="2.5" fill="currentColor" opacity=".3"/>
  </svg>`;
}

/* ── Worker ────────────────────────────────────────────────────── */

function initWorker(): void {
  worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  worker.onmessage = (ev: MessageEvent) => {
    const m = ev.data;
    const job = jobs.get(m.jobId);
    if (m.type === 'log') {
      ingest(m.event as LogEvent);
      return;
    }
    if (!job) return;
    const model = findModel(job.modelId);

    if (m.type === 'stage') {
      setBusyLabel(job.modelId, m.label);
      return;
    }
    if (m.type === 'progress') {
      if (m.phase === 'download' && m.total) {
        setBusyLabel(job.modelId, `Downloading tokenizer · ${percent(m.received / m.total)}`);
      } else if (m.phase === 'attribute') {
        setBusyLabel(job.modelId, `Measuring segments · ${percent(m.fraction)}`);
      }
      return;
    }
    if (m.type === 'result' && job.purpose === 'compare') {
      state.busy.delete(job.modelId);
      state.previousPrompt = { label: job.label ?? 'the second render', modelId: job.modelId, ids: m.ids, tokens: m.tokens };
      jobs.delete(m.jobId);
      log.render(`Comparison render: ${commas(m.total)} tokens`, 'ok');
      renderModelPicker();
      renderResults();
      return;
    }
    if (m.type === 'result') {
      state.busy.delete(job.modelId);
      state.failures.delete(job.modelId);
      state.results.set(job.modelId, {
        modelId: job.modelId,
        label: model?.label ?? job.modelId,
        estimate: false,
        ...m,
      } as ModelResult);
      log.tokenize(`${model?.label ?? job.modelId}: ${commas(m.total)} tokens`, 'ok');
      if (state.budget === 0) state.budget = Math.max(512, Math.round(m.total * 0.6));
      jobs.delete(m.jobId);
      renderModelPicker();
      renderResults();
      return;
    }
    if (m.type === 'render-failed') {
      state.busy.delete(job.modelId);
      state.results.delete(job.modelId);
      state.failures.set(job.modelId, {
        modelId: job.modelId,
        label: model?.label ?? job.modelId,
        error: m.error,
        kind: m.kind,
        template: m.template,
      });
      log.render(`${model?.label ?? job.modelId}: ${m.error}`, 'warn');
      jobs.delete(m.jobId);
      renderModelPicker();
      renderResults();
      return;
    }
    if (m.type === 'error') {
      state.busy.delete(job.modelId);
      state.failures.set(job.modelId, {
        modelId: job.modelId,
        label: model?.label ?? job.modelId,
        error: m.error,
        kind: 'network',
      });
      log.network(`${model?.label ?? job.modelId}: ${m.error}`, 'err');
      jobs.delete(m.jobId);
      renderModelPicker();
      renderResults();
    }
  };
  worker.onerror = (e) => {
    log.system(`Worker failed: ${e.message}`, 'err');
    toast('Something failed in the background worker.', 'err');
  };
}

function analyseModel(modelId: string): void {
  const conv = state.conversation;
  if (!conv) return;

  const local = findLocalEncoding(modelId);
  if (local) {
    void analyseLocal(modelId);
    return;
  }
  const model = findModel(modelId);
  if (!model || !worker) return;

  const jobId = ++jobCounter;
  jobs.set(jobId, { modelId, purpose: 'primary' });
  state.busy.add(modelId);
  state.failures.delete(modelId);
  setBusyLabel(modelId, 'Starting');
  worker.postMessage({
    type: 'analyse',
    jobId,
    repo: model.hfRepo,
    messages: conv.messages,
    tools: conv.tools,
    addGenerationPrompt: state.addGenerationPrompt,
    templateName: null,
    nowMs: Date.now(),
  });
  renderModelPicker();
}

/**
 * Render the identical request again with the clock moved on a day, and keep
 * the result as the thing to diff against.
 *
 * This is not a simulation: it is a real second render through the same
 * template. For a template that injects the date — Llama 3.2 does — the two
 * token arrays genuinely diverge a few tokens in, which is the cleanest
 * demonstration there is that a prompt can break its own cache with no help
 * from your code. For a template that does not, the answer is "identical", and
 * that is worth showing too.
 */
function renderTomorrow(): void {
  const conv = state.conversation;
  const modelId = state.selected[0];
  const model = findModel(modelId);
  if (!conv || !model || !worker) return;

  const jobId = ++jobCounter;
  jobs.set(jobId, { modelId, purpose: 'compare', label: 'the same request rendered tomorrow' });
  state.busy.add(modelId);
  setBusyLabel(modelId, 'Rendering tomorrow');
  worker.postMessage({
    type: 'analyse',
    jobId,
    repo: model.hfRepo,
    messages: conv.messages,
    tools: conv.tools,
    addGenerationPrompt: state.addGenerationPrompt,
    templateName: null,
    nowMs: Date.now() + 86_400_000,
  });
  renderModelPicker();
}

/**
 * The bundled OpenAI encodings. Dynamically imported so a visitor who never
 * selects one does not pay for roughly a megabyte of vocabulary.
 */
async function analyseLocal(modelId: string): Promise<void> {
  const spec = findLocalEncoding(modelId);
  const conv = state.conversation;
  if (!spec || !conv) return;

  state.busy.add(modelId);
  setBusyLabel(modelId, 'Loading encoding');
  renderModelPicker();

  try {
    const mod =
      spec.encoding === 'o200k_base'
        ? await import('gpt-tokenizer/encoding/o200k_base')
        : await import('gpt-tokenizer/encoding/cl100k_base');
    const encode = (mod as unknown as { encode: (s: string) => number[] }).encode;

    // There is no public chat template for these models, so we apply OpenAI's
    // documented per-message accounting instead of inventing a framing. The UI
    // says so next to every number this produces.
    const { OPENAI_TOKENS_PER_MESSAGE, OPENAI_TOKENS_PER_NAME, OPENAI_REPLY_PRIMING } = await import('./tokenize');

    const segments: Segment[] = [];
    let total = OPENAI_REPLY_PRIMING;
    const cuts: number[] = [0];

    if (conv.tools.length) {
      const t = encode(JSON.stringify(conv.tools)).length;
      total += t;
      segments.push({
        id: 'tools',
        kind: 'tools',
        label: `Tool definitions · ${conv.tools.length}`,
        messageIndex: -1,
        tokens: t,
        chars: JSON.stringify(conv.tools).length,
        preview: conv.tools.map((x) => x.function?.name ?? x.name ?? '').join(', '),
      });
    }

    conv.messages.forEach((m, k) => {
      const text = typeof m.content === 'string' ? m.content : JSON.stringify(m.content ?? '');
      const extra = m.tool_calls ? encode(JSON.stringify(m.tool_calls)).length : 0;
      const t = encode(text).length + extra + OPENAI_TOKENS_PER_MESSAGE + (m.name ? OPENAI_TOKENS_PER_NAME : 0);
      total += t;
      segments.push({
        id: `msg:${k}`,
        kind: String(m.role) === 'system' ? 'system' : String(m.role) === 'tool' ? 'document' : 'turn',
        label: `${String(m.role)} · turn ${k + 1}`,
        messageIndex: k,
        role: String(m.role),
        tokens: t,
        chars: text.length,
        preview: preview(text),
        retrieved: String(m.role) === 'tool',
      });
      cuts.push(total);
    });

    segments.push({
      id: 'overhead',
      kind: 'overhead',
      label: 'Reply priming',
      messageIndex: -1,
      tokens: OPENAI_REPLY_PRIMING,
      chars: 0,
      preview: 'The allowance OpenAI documents for priming the assistant reply.',
    });

    state.busy.delete(modelId);
    state.results.set(modelId, {
      modelId,
      label: spec.label,
      prompt: '',
      ids: [],
      tokens: [],
      segments,
      toolSpans: [],
      cuts,
      total,
      skipped: 0,
      degenerate: false,
      approximateBoundaries: false,
      rollup: rollupSegments(segments, []),
      contextWindow: spec.contextWindow,
      tokenizerBytes: 0,
      templateName: 'documented accounting',
      availableTemplates: [],
      template: '',
      capabilities: {
        tools: true,
        toolCalls: true,
        documents: false,
        generationPrompt: false,
        injectsDate: false,
        generationSpans: false,
      },
      loss: { trained: [], source: 'assistant-segments', trainedTokens: 0, totalTokens: total, anomalies: [] },
      estimate: spec.estimate,
    });
    log.tokenize(`${spec.label}: ${commas(total)} tokens (${spec.estimate ? 'estimate' : 'documented accounting'})`, 'ok');
  } catch (e) {
    state.busy.delete(modelId);
    state.failures.set(modelId, { modelId, label: spec.label, error: (e as Error).message, kind: 'load' });
  }
  renderModelPicker();
  renderResults();
}

function setBusyLabel(modelId: string, label: string): void {
  const chip = els.modelChips.querySelector<HTMLElement>(`[data-model="${CSS.escape(modelId)}"] .chip-state`);
  if (chip) chip.textContent = label;
}

/* ── Input ─────────────────────────────────────────────────────── */

function wireEvents(): void {
  document.getElementById('nav-how')!.addEventListener('click', showHowItWorks);
  document.getElementById('nav-privacy')!.addEventListener('click', showPrivacy);
  document.getElementById('nav-about')!.addEventListener('click', showAbout);
  document.getElementById('nav-log')!.addEventListener('click', toggleDrawer);

  document.getElementById('btn-sample')!.addEventListener('click', (e) => {
    e.stopPropagation();
    void loadSample();
  });
  document.getElementById('btn-paste')!.addEventListener('click', async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) ingestText(text, 'clipboard');
      else toast('The clipboard is empty.', 'warn');
    } catch {
      els.input.focus();
      toast('Press Cmd/Ctrl+V to paste into the box.', 'warn');
    }
  });
  document.getElementById('btn-clear')!.addEventListener('click', () => {
    els.input.value = '';
    state.conversation = null;
    state.results.clear();
    state.failures.clear();
    state.budget = 0;
    els.inputStatus.textContent = '';
    els.inputStatus.className = 'status';
    renderResults();
  });

  let debounce = 0;
  els.input.addEventListener('input', () => {
    window.clearTimeout(debounce);
    debounce = window.setTimeout(() => ingestText(els.input.value, 'typed'), 350);
  });

  els.dropzone.addEventListener('dragover', (e) => {
    e.preventDefault();
    els.dropzone.classList.add('over');
  });
  els.dropzone.addEventListener('dragleave', () => els.dropzone.classList.remove('over'));
  els.dropzone.addEventListener('drop', async (e) => {
    e.preventDefault();
    els.dropzone.classList.remove('over');
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const text = await file.text();
    els.input.value = text;
    ingestText(text, `file ${file.name}`);
  });

  els.modelAdd.addEventListener('change', () => {
    const id = els.modelAdd.value;
    els.modelAdd.value = '';
    if (!id || state.selected.includes(id)) return;
    if (state.selected.length >= 4) {
      toast('Four models at a time is the limit.', 'warn');
      return;
    }
    state.selected.push(id);
    renderModelPicker();
    if (state.conversation) analyseModel(id);
  });

  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      if (state.conversation) reanalyseAll();
    }
  });
}

function ingestText(text: string, source: string): void {
  const trimmed = text.trim();
  state.rawInput = trimmed;
  if (!trimmed) {
    state.conversation = null;
    els.inputStatus.textContent = '';
    els.inputStatus.className = 'status';
    renderResults();
    return;
  }

  try {
    // Keep the previous prompt so the cache view has something real to diff.
    const first = state.results.get(state.selected[0]);
    if (first && first.ids.length) {
      state.previousPrompt = { label: 'your previous input', modelId: first.modelId, ids: first.ids, tokens: first.tokens };
    }

    const conv = parseConversation(trimmed);
    state.conversation = conv;
    const odd = unusualRoles(conv.messages);
    els.inputStatus.className = 'status ok';
    els.inputStatus.innerHTML = `${icon('check')} Parsed ${conv.messages.length} message${conv.messages.length === 1 ? '' : 's'} and ${conv.tools.length} tool definition${conv.tools.length === 1 ? '' : 's'} from ${escapeHtml(source)}.${
      odd.length ? ` <strong>Note:</strong> role${odd.length > 1 ? 's' : ''} ${odd.map((r) => `“${escapeHtml(r)}”`).join(', ')} — most templates drop these silently.` : ''
    }`;
    log.system(`Parsed ${conv.messages.length} messages, ${conv.tools.length} tools (${source}).`, 'ok');
    state.budget = 0;
    reanalyseAll();
  } catch (e) {
    const err = e as ParseError;
    state.conversation = null;
    els.inputStatus.className = 'status err';
    els.inputStatus.innerHTML = `${icon('alert')} ${escapeHtml(err.message)}${err.hint ? ` <span class="hint">${escapeHtml(err.hint)}</span>` : ''}`;
    renderResults();
  }
}

function reanalyseAll(): void {
  state.results.clear();
  state.failures.clear();
  for (const id of state.selected) analyseModel(id);
  renderResults();
}

async function loadSample(): Promise<void> {
  try {
    log.network('GET /samples/agent-conversation.messages.json');
    const res = await fetch('samples/agent-conversation.messages.json');
    const text = await res.text();
    els.input.value = text;
    // Deliberately the same path a real paste takes — no shortcut.
    ingestText(text, 'the bundled sample');
  } catch {
    toast('Could not load the sample.', 'err');
  }
}

/* ── Model picker ──────────────────────────────────────────────── */

function renderModelPicker(): void {
  els.modelChips.innerHTML = state.selected
    .map((id) => {
      const model = findModel(id);
      const local = findLocalEncoding(id);
      const label = model?.label ?? local?.label ?? id;
      const result = state.results.get(id);
      const fail = state.failures.get(id);
      const busy = state.busy.has(id);
      const cls = fail ? 'chip err' : busy ? 'chip busy' : result ? 'chip ok' : 'chip';
      const value = result
        ? `${commas(result.total)} tokens`
        : fail
          ? 'unavailable'
          : busy
            ? 'working…'
            : 'idle';
      return `<div class="${cls}" data-model="${escapeHtml(id)}">
        <span class="chip-name">${escapeHtml(label)}</span>
        <span class="chip-state">${escapeHtml(value)}</span>
        ${local?.estimate ? '<span class="chip-badge estimate">estimate</span>' : ''}
        ${model?.mirrorOf ? '<span class="chip-badge mirror" title="Community mirror of a gated repository">mirror</span>' : ''}
        ${state.selected.length > 1 ? `<button type="button" class="chip-x" data-remove="${escapeHtml(id)}" aria-label="Remove ${escapeHtml(label)}">${icon('x')}</button>` : ''}
      </div>`;
    })
    .join('');

  els.modelChips.querySelectorAll<HTMLButtonElement>('[data-remove]').forEach((b) =>
    b.addEventListener('click', () => {
      const id = b.dataset.remove!;
      state.selected = state.selected.filter((x) => x !== id);
      state.results.delete(id);
      state.failures.delete(id);
      renderModelPicker();
      renderResults();
    }),
  );

  const options = [
    '<option value="">Add a model…</option>',
    '<optgroup label="Open models — exact, template rendered live">',
    ...MODELS.filter((m) => !state.selected.includes(m.id)).map(
      (m) => `<option value="${m.id}">${escapeHtml(m.label)} · ${bytes(m.tokenizerBytes)} tokenizer</option>`,
    ),
    '</optgroup>',
    '<optgroup label="OpenAI encodings — documented accounting">',
    ...LOCAL_ENCODINGS.filter((m) => !state.selected.includes(m.id)).map(
      (m) =>
        `<option value="${m.id}">${escapeHtml(m.label)} · ${m.encoding === 'o200k_base' ? '~1 MB' : '~440 KB'} vocabulary</option>`,
    ),
    '</optgroup>',
  ].join('');
  els.modelAdd.innerHTML = options;

  const primary = findModel(state.selected[0]);
  els.modelNote.innerHTML = primary
    ? `The tokenizer for <strong>${escapeHtml(primary.label)}</strong> is a ${bytes(primary.tokenizerBytes)} download from Hugging Face, once. After that it is cached and chatprint works offline.${
        primary.mirrorOf
          ? ` This repo is a community mirror of <code>${escapeHtml(primary.mirrorOf)}</code>, which is gated and cannot be read without an account.`
          : ''
      }`
    : '';
}

/* ── Views ─────────────────────────────────────────────────────── */

const TABS: Array<{ id: typeof state.view; label: string; icon: string }> = [
  { id: 'prompt', label: 'Prompt', icon: 'terminal' },
  { id: 'budget', label: 'Budget', icon: 'layers' },
  { id: 'compare', label: 'Compare', icon: 'git-compare' },
  { id: 'cache', label: 'Cache', icon: 'database' },
  { id: 'training', label: 'Training', icon: 'scissors' },
];

function renderResults(): void {
  const hasAny = state.results.size > 0 || state.failures.size > 0 || state.busy.size > 0;
  els.results.hidden = !state.conversation && !hasAny;
  if (els.results.hidden) return;

  els.tabs.innerHTML = TABS.map(
    (t) =>
      `<button type="button" role="tab" class="tab${state.view === t.id ? ' active' : ''}" data-tab="${t.id}" aria-selected="${state.view === t.id}">${icon(t.icon)}<span>${t.label}</span></button>`,
  ).join('');
  els.tabs.querySelectorAll<HTMLButtonElement>('[data-tab]').forEach((b) =>
    b.addEventListener('click', () => {
      state.view = b.dataset.tab as typeof state.view;
      renderResults();
    }),
  );

  const primary = state.results.get(state.selected[0]);
  const fail = state.failures.get(state.selected[0]);

  if (!primary && fail) {
    els.view.innerHTML = failureCard(fail);
    wireGlossary(els.view);
    return;
  }
  if (!primary) {
    els.view.innerHTML = `<div class="empty">${state.busy.size ? 'Working…' : 'Paste a request, or press <strong>Try a sample</strong>.'}</div>`;
    return;
  }

  if (state.view === 'prompt') renderPromptView(primary);
  else if (state.view === 'budget') renderBudgetView(primary);
  else if (state.view === 'compare') renderCompareView();
  else if (state.view === 'cache') renderCacheView(primary);
  else renderTrainingView(primary);

  wireGlossary(els.view);
}

function failureCard(f: FailedResult): string {
  return `<div class="failure">
    <h3>${icon('alert')} ${escapeHtml(f.label)} refused this conversation</h3>
    <p class="raise">${escapeHtml(f.error)}</p>
    <p class="muted">That message was written by the people who published the model's chat template — it is
    the template rejecting the shape of your conversation, not chatprint failing. Common causes: two tool calls
    in one assistant turn where the model allows only one, a system message on a template that forbids one, or
    roles that do not alternate. Fix the conversation, or pick another model to see what it would cost there.</p>
    ${f.template ? `<details><summary>Show the template source</summary><pre class="template">${escapeHtml(f.template)}</pre></details>` : ''}
  </div>`;
}

function fitBadge(total: number, window: number | null): string {
  if (!window) return '<span class="badge">context window unknown</span>';
  const share = total / window;
  const cls = total > window ? 'bad' : share > 0.8 ? 'warn' : 'good';
  const text =
    total > window
      ? `over by ${commas(total - window)}`
      : `${percent(share)} of ${compactTokens(window)}`;
  return `<span class="badge ${cls}">${text}</span>`;
}

function renderPromptView(r: ModelResult): void {
  if (!r.prompt) {
    els.view.innerHTML = `<div class="notice">${icon('info')} This is a bundled encoding, so there is no rendered
      template to show — OpenAI does not publish the framing its API wraps around your messages. The Budget tab
      still gives you a per-segment breakdown using their documented accounting.</div>`;
    return;
  }

  const spans = buildSpans(r);
  const capsMissing: string[] = [];
  if (state.conversation?.tools.length && !r.capabilities.tools) {
    capsMissing.push(
      `<strong>This template ignores tool definitions.</strong> Your ${state.conversation.tools.length} tools are not in this prompt at all, so this model would not know they exist. That is why its count is so much lower than a model that renders them.`,
    );
  }
  if (r.skipped > 0) {
    capsMissing.push(
      `${r.skipped} prefix render${r.skipped === 1 ? '' : 's'} were refused by this template, so those messages share a segment boundary with the next one. The total is unaffected.`,
    );
  }
  if (r.degenerate) {
    capsMissing.push(
      `<strong>The per-segment split could not be established for this template.</strong> The total of ${commas(r.total)} tokens is correct, but the breakdown below is not trustworthy and is hidden.`,
    );
  }

  els.view.innerHTML = `
    <div class="view-head">
      <div>
        <h3>The literal string ${escapeHtml(r.label)} receives</h3>
        <p class="muted">${commas(r.prompt.length)} characters · ${commas(r.total)} ${glossaryTerm('token', 'tokens')} · template <code>${escapeHtml(r.templateName)}</code></p>
      </div>
      <div class="view-actions">
        ${fitBadge(r.total, r.contextWindow)}
        <button type="button" class="pill" id="copy-prompt">${icon('copy')} Copy</button>
        <button type="button" class="pill" id="save-prompt">${icon('download')} rendered-prompt.txt</button>
      </div>
    </div>
    ${capsMissing.map((c) => `<div class="notice">${icon('info')} ${c}</div>`).join('')}
    <div class="legend">${legend(r)}</div>
    <pre class="prompt" id="prompt-render">${spans}</pre>
    <details class="template-details"><summary>Show this model's chat template (${commas(r.template.length)} characters of Jinja)</summary><pre class="template">${escapeHtml(r.template)}</pre></details>
  `;

  document.getElementById('copy-prompt')?.addEventListener('click', async () => {
    if (await copyText(r.prompt)) toast('Rendered prompt copied.');
  });
  document.getElementById('save-prompt')?.addEventListener('click', () => {
    void saveText('rendered-prompt.txt', r.prompt);
  });
}

/**
 * Colour the rendered prompt by segment. Built from the token spans rather
 * than by re-splitting the string, so what you see is what was counted.
 */
function buildSpans(r: ModelResult): string {
  if (r.degenerate) return escapeHtml(r.prompt);

  const MAX_TOKENS = 24_000; // beyond this the DOM cost stops being worth it
  const truncated = r.tokens.length > MAX_TOKENS;
  const limit = truncated ? MAX_TOKENS : r.tokens.length;

  const kindAt = new Array<string>(r.tokens.length).fill('overhead');
  r.segments.forEach((s, i) => {
    const start = i === 0 && s.id === 'preamble' ? 0 : s.messageIndex >= 0 ? r.cuts[s.messageIndex] : -1;
    const end = s.messageIndex >= 0 ? r.cuts[s.messageIndex + 1] : -1;
    if (start >= 0 && end > start) for (let k = start; k < end && k < kindAt.length; k++) kindAt[k] = s.kind;
  });
  for (const t of r.toolSpans) for (let k = t.start; k < t.end && k < kindAt.length; k++) kindAt[k] = 'tools';

  let html = '';
  let runKind = kindAt[0];
  let buf = '';
  for (let i = 0; i < limit; i++) {
    const k = kindAt[i];
    if (k !== runKind) {
      html += `<span class="seg ${runKind}">${escapeHtml(buf)}</span>`;
      buf = '';
      runKind = k;
    }
    buf += r.tokens[i];
  }
  html += `<span class="seg ${runKind}">${escapeHtml(buf)}</span>`;
  if (truncated) {
    html += `<span class="seg overhead">\n\n… ${commas(r.tokens.length - MAX_TOKENS)} more tokens not drawn (the counts above cover all of them).</span>`;
  }
  // Tokenizer artefacts: the byte-level marker for a space, and SentencePiece's.
  return html.replace(/Ġ/g, ' ').replace(/▁/g, ' ').replace(/Ċ/g, '\n');
}

function legend(r: ModelResult): string {
  const items: Array<[string, string, number]> = [
    ['system', 'System prompt', r.rollup.system],
    ['tools', 'Tool definitions', r.rollup.tools],
    ['document', 'Retrieved / tool results', r.rollup.documents],
    ['turn', 'Conversation', r.rollup.history],
    ['overhead', glossaryTerm('template overhead', 'Template overhead'), r.rollup.overhead],
  ];
  return items
    .filter(([, , v]) => v > 0)
    .map(
      ([k, label, v]) =>
        `<span class="legend-item"><i class="swatch ${k}"></i>${label} <strong>${commas(v)}</strong> <em>${percent(v / r.total)}</em></span>`,
    )
    .join('');
}

function renderBudgetView(r: ModelResult): void {
  const sum = r.segments.reduce((a, s) => a + s.tokens, 0);
  if (sum !== r.total) {
    els.view.innerHTML = `<div class="failure"><h3>${icon('alert')} The breakdown does not add up</h3>
      <p class="muted">The segments sum to ${commas(sum)} but the prompt is ${commas(r.total)} tokens. chatprint will not
      draw a breakdown it cannot vouch for. The total is correct; the split is not.</p></div>`;
    return;
  }

  const budget = state.budget || Math.round(r.total * 0.6);
  const fixed = r.segments.filter((s) => s.messageIndex < 0).reduce((a, s) => a + s.tokens, 0) + r.rollup.tools;
  const plan = planPack({
    messages: state.conversation?.messages ?? [],
    segments: r.segments,
    fixedTokens: fixed,
    budget,
    strategy: state.strategy,
  });
  const droppedSet = new Set(plan.dropped);

  const rows = r.segments
    .filter((s) => s.tokens > 0)
    .sort((a, b) => b.tokens - a.tokens)
    .map((s) => {
      const carved = r.toolSpans
        .filter((t) => t.parentIndex >= 0 && r.segments[t.parentIndex]?.id === s.id)
        .reduce((a, t) => a + t.tokens, 0);
      return `<tr class="${droppedSet.has(s.messageIndex) ? 'dropped' : ''}">
        <td><i class="swatch ${s.kind}"></i>${escapeHtml(s.label)}${carved ? `<span class="sub">includes ${commas(carved)} tokens of tool definitions the template spliced in here</span>` : ''}${s.preview ? `<span class="sub muted">${escapeHtml(s.preview)}</span>` : ''}</td>
        <td class="num">${commas(s.tokens)}</td>
        <td class="num">${percent(s.tokens / r.total)}</td>
        <td class="bar"><i style="width:${(s.tokens / r.total) * 100}%" class="swatch ${s.kind}"></i></td>
      </tr>`;
    })
    .join('');

  els.view.innerHTML = `
    <div class="view-head">
      <div>
        <h3>Where the ${commas(r.total)} tokens went</h3>
        <p class="muted">${escapeHtml(r.label)} · every row measured, and they sum to the total exactly.
        ${r.approximateBoundaries ? 'Some boundaries are placed within one token.' : ''}</p>
      </div>
      <div class="view-actions">
        ${fitBadge(r.total, r.contextWindow)}
        <button type="button" class="pill" id="save-csv">${icon('download')} token-budget.csv</button>
      </div>
    </div>

    <div class="stacked">${['system', 'tools', 'document', 'turn', 'overhead']
      .map((k) => {
        const v =
          k === 'system' ? r.rollup.system : k === 'tools' ? r.rollup.tools : k === 'document' ? r.rollup.documents : k === 'turn' ? r.rollup.history : r.rollup.overhead;
        return v > 0 ? `<i class="swatch ${k}" style="flex:${v}" title="${escapeHtml(k)}: ${commas(v)}"></i>` : '';
      })
      .join('')}</div>
    <div class="legend">${legend(r)}</div>

    <table class="budget"><thead><tr><th>Segment</th><th class="num">Tokens</th><th class="num">Share</th><th></th></tr></thead><tbody>${rows}</tbody></table>

    <div class="packer">
      <h4>${icon('scissors')} Pack it down</h4>
      <div class="packer-controls">
        <label>Budget
          <input type="range" id="budget" min="256" max="${Math.max(1024, r.contextWindow ?? r.total * 2)}" step="128" value="${budget}" />
          <output id="budget-out">${commas(budget)}</output>
        </label>
        <label>Drop order
          <select id="strategy">
            <option value="oldest-first"${state.strategy === 'oldest-first' ? ' selected' : ''}>Oldest turns first</option>
            <option value="documents-first"${state.strategy === 'documents-first' ? ' selected' : ''}>Biggest retrieved documents first</option>
          </select>
        </label>
      </div>
      <p class="packer-result ${plan.impossible ? 'bad' : 'good'}">
        ${plan.impossible ? icon('alert') : icon('check')}
        ${escapeHtml(plan.notes.join(' ') || 'Everything already fits — nothing needs dropping.')}
        ${plan.dropped.length ? `Estimated ${commas(plan.estimate)} tokens after trimming.` : ''}
      </p>
      <button type="button" class="pill primary" id="save-trimmed" ${plan.impossible ? 'disabled' : ''}>${icon('download')} messages.trimmed.json</button>
    </div>
  `;

  const slider = document.getElementById('budget') as HTMLInputElement;
  slider?.addEventListener('input', () => {
    state.budget = Number(slider.value);
    (document.getElementById('budget-out') as HTMLOutputElement).value = commas(state.budget);
  });
  slider?.addEventListener('change', () => renderResults());
  document.getElementById('strategy')?.addEventListener('change', (e) => {
    state.strategy = (e.target as HTMLSelectElement).value as PackStrategy;
    renderResults();
  });

  document.getElementById('save-csv')?.addEventListener('click', () => {
    const columns: BudgetColumn[] = [...state.results.values()].map((res) => ({
      label: res.label,
      tokens: new Map(res.segments.map((s) => [s.id, s.tokens])),
      total: res.total,
      estimate: res.estimate,
    }));
    void saveText('token-budget.csv', budgetCsv(r.segments, columns), 'text/csv');
  });

  document.getElementById('save-trimmed')?.addEventListener('click', () => {
    const messages = applyPlan(state.conversation?.messages ?? [], plan);
    const problems = validateConversation(messages);
    if (problems.length) {
      toast(problems[0], 'err');
      return;
    }
    void saveText(
      'messages.trimmed.json',
      trimmedJson({ messages, tools: state.conversation?.tools as ToolDefinition[] }),
      'application/json',
    );
  });
}

function renderCompareView(): void {
  const rows = state.selected
    .map((id) => state.results.get(id))
    .filter((r): r is ModelResult => Boolean(r))
    .map((r) => ({ label: r.label, total: r.total, contextWindow: r.contextWindow, estimate: r.estimate, note: r.capabilities.tools ? undefined : 'template ignores tool definitions' }));

  if (rows.length < 2) {
    els.view.innerHTML = `<div class="empty">Add a second model above to compare. The same conversation can differ by
      three times between models — usually because some templates render your tool definitions and some silently drop them.</div>`;
    return;
  }

  const cmp = compareModels(rows);
  els.view.innerHTML = `
    <div class="view-head"><div><h3>The same conversation, ${cmp.length} models</h3>
    <p class="muted">Deltas are against ${escapeHtml(cmp[0].label)}.</p></div></div>
    <table class="budget"><thead><tr><th>Model</th><th class="num">Tokens</th><th class="num">Δ</th><th class="num">Window</th><th>Fit</th></tr></thead>
    <tbody>${cmp
      .map(
        (c) => `<tr>
        <td>${escapeHtml(c.label)}${c.estimate ? '<span class="chip-badge estimate">estimate</span>' : ''}${c.note ? `<span class="sub muted">${escapeHtml(c.note)}</span>` : ''}</td>
        <td class="num">${commas(c.total)}</td>
        <td class="num ${c.delta > 0 ? 'up' : c.delta < 0 ? 'down' : ''}">${c.delta === 0 ? '—' : `${c.delta > 0 ? '+' : ''}${commas(c.delta)}`}</td>
        <td class="num">${c.contextWindow ? compactTokens(c.contextWindow) : '—'}</td>
        <td>${fitBadge(c.total, c.contextWindow)}</td>
      </tr>`,
      )
      .join('')}</tbody></table>
    ${state.failures.size ? `<div class="notice">${icon('info')} ${[...state.failures.values()].map((f) => `<strong>${escapeHtml(f.label)}</strong> refused the conversation: ${escapeHtml(f.error)}`).join('<br />')}</div>` : ''}
  `;
}

function renderCacheView(r: ModelResult): void {
  if (!r.ids.length) {
    els.view.innerHTML = `<div class="notice">${icon('info')} Pick an open model to use this view — it needs real token ids, which the bundled encodings do not produce for a chat request.</div>`;
    return;
  }

  // A comparison captured under a different model is meaningless here: token
  // ids are only comparable within one tokenizer.
  const cmp = state.previousPrompt?.modelId === r.modelId ? state.previousPrompt : null;
  if (!cmp) {
    els.view.innerHTML = `
      <div class="view-head"><div>
        <h3>${glossaryTerm('prompt caching', 'Prompt cache')} reuse</h3>
        <p class="muted">Find the exact token where two versions of a request stop matching. Everything before it can
        be reused by a provider's prefix cache; everything after it is paid for again.</p>
      </div></div>
      <div class="empty">
        <p>There is nothing to compare against yet.</p>
        <p class="muted">Edit your request above and chatprint will diff the new version against the old one — or
        render this same request again with the clock moved on a day, which is how you find out whether the
        <em>template itself</em> is breaking your cache.</p>
        <p style="margin-top:14px"><button type="button" class="pill primary" id="render-tomorrow">Render this again with tomorrow's date</button></p>
      </div>`;
    document.getElementById('render-tomorrow')?.addEventListener('click', renderTomorrow);
    return;
  }
  const b = cmp;
  const d = diverge(r.ids, b.ids);
  const toolRange = r.toolSpans.length
    ? { start: Math.min(...r.toolSpans.map((t) => t.start)), end: Math.max(...r.toolSpans.map((t) => t.end)) }
    : null;
  const systemSeg = r.segments.find((s) => s.kind === 'system');
  const systemEnd = systemSeg && systemSeg.messageIndex >= 0 ? r.cuts[systemSeg.messageIndex + 1] : null;
  const breakers = diagnose({ a: { ids: r.ids, tokens: r.tokens }, b: { ids: b.ids, tokens: b.tokens }, divergence: d, toolRange, systemEnd });

  els.view.innerHTML = `
    <div class="view-head"><div>
      <h3>${glossaryTerm('prompt caching', 'Prompt cache')} reuse</h3>
      <p class="muted">Comparing this prompt against ${escapeHtml(b.label)}.</p>
    </div></div>

    ${
      r.capabilities.injectsDate
        ? `<div class="notice warn">${icon('alert')} <strong>This template injects the current date into your prompt.</strong>
           That means the prompt changes every day on its own, near the beginning, and a provider's prefix cache is
           invalidated from that point onward — through no fault of your code. It is the cleanest real example of a
           ${glossaryTerm('cache breaker')} there is.</div>`
        : ''
    }

    <div class="cache-bar">
      <i class="reuse" style="width:${(d.reusable * 100).toFixed(2)}%"></i>
      <i class="lost" style="width:${(100 - d.reusable * 100).toFixed(2)}%"></i>
    </div>
    <div class="cache-stats">
      <div><span>Shared prefix</span><strong>${commas(d.lcp)} tokens</strong></div>
      <div><span>Reusable</span><strong>${percent(d.reusable)}</strong></div>
      <div><span>Lost from cache</span><strong>${commas(d.aTail)} tokens</strong></div>
    </div>

    ${breakers
      .map(
        (bk) => `<div class="breaker ${bk.confidence}">
        <h4>${escapeHtml(bk.title)} <span class="chip-badge">${bk.confidence === 'observed' ? 'measured' : 'likely cause'}</span></h4>
        <p>${escapeHtml(bk.detail)}</p>
        <p class="fix">${escapeHtml(bk.fix)}</p>
      </div>`,
      )
      .join('')}

    <div class="notice">${icon('info')} What this cannot tell you: whether the provider's cache entry had simply
      expired. Cached prefixes live for minutes, and two byte-identical requests can still miss. Rule that out
      separately — a token diff can never see it.</div>
    <p><button type="button" class="pill" id="render-tomorrow">Compare against tomorrow's render instead</button></p>
  `;
  document.getElementById('render-tomorrow')?.addEventListener('click', renderTomorrow);
}

function renderTrainingView(r: ModelResult): void {
  if (!r.ids.length) {
    els.view.innerHTML = `<div class="notice">${icon('info')} Pick an open model to use this view.</div>`;
    return;
  }
  const loss = r.loss;
  const share = r.total ? loss.trainedTokens / r.total : 0;

  els.view.innerHTML = `
    <div class="view-head"><div>
      <h3>${glossaryTerm('loss mask', 'Loss mask')}</h3>
      <p class="muted">Which tokens a fine-tuning run would learn from. Source:
        <strong>${loss.source === 'generation-tags' ? "the template's own {% generation %} markers" : 'the assistant turns, matched to segment boundaries'}</strong>.</p>
    </div></div>

    <div class="cache-stats">
      <div><span>Trained on</span><strong>${commas(loss.trainedTokens)} tokens</strong></div>
      <div><span>Masked out</span><strong>${commas(r.total - loss.trainedTokens)} tokens</strong></div>
      <div><span>Share trained</span><strong>${percent(share)}</strong></div>
    </div>

    ${loss.anomalies
      .map(
        (a) => `<div class="notice ${a.severity === 'err' ? 'warn' : ''}">${icon('alert')} ${escapeHtml(a.message)}</div>`,
      )
      .join('')}
    ${loss.anomalies.length === 0 ? `<div class="notice">${icon('check')} No anomalies found: every assistant turn has content, the end-of-turn token is inside the loss, and nothing trains on the generation prompt.</div>` : ''}

    <div class="legend"><span class="legend-item"><i class="swatch trained"></i>trained</span><span class="legend-item"><i class="swatch masked"></i>masked (-100)</span></div>
    <pre class="prompt">${lossSpans(r)}</pre>
  `;
}

function lossSpans(r: ModelResult): string {
  const MAX = 20_000;
  const limit = Math.min(r.tokens.length, MAX);
  const trained = new Array<boolean>(limit).fill(false);
  for (const s of r.loss.trained) for (let i = s.start; i < s.end && i < limit; i++) trained[i] = true;

  let html = '';
  let run = trained[0];
  let buf = '';
  for (let i = 0; i < limit; i++) {
    if (trained[i] !== run) {
      html += `<span class="seg ${run ? 'trained' : 'masked'}">${escapeHtml(buf)}</span>`;
      buf = '';
      run = trained[i];
    }
    buf += r.tokens[i];
  }
  html += `<span class="seg ${run ? 'trained' : 'masked'}">${escapeHtml(buf)}</span>`;
  if (r.tokens.length > MAX) html += `<span class="seg masked">\n\n… ${commas(r.tokens.length - MAX)} more tokens not drawn.</span>`;
  return html.replace(/Ġ/g, ' ').replace(/▁/g, ' ').replace(/Ċ/g, '\n');
}

/* ── Modals ────────────────────────────────────────────────────── */

function showHowItWorks(): void {
  openModal(
    'How it works',
    `
    <ol class="steps">
      <li><h4>You paste the request your code was about to send</h4>
        <p>An OpenAI or Anthropic request body, or a bare messages array. It is parsed in the tab. It is not uploaded,
        and there is no server here to upload it to.</p></li>
      <li><h4>chatprint downloads the model's own tokenizer</h4>
        <p>Every open model publishes two small files next to its multi-gigabyte weights: <code>tokenizer.json</code>
        and <code>tokenizer_config.json</code>. Downloading them is enough to count tokens exactly, without touching
        the model. This is the only network request the page makes, it is anonymous, and it is cached so the second
        visit works offline.</p></li>
      <li><h4>It renders your messages through the model's real ${glossaryTerm('chat template')}</h4>
        <p>The template is a Jinja program shipped inside <code>tokenizer_config.json</code>. It is what turns your
        JSON into the single flat string the model actually sees — turn markers, headers, tool definitions and all.
        Everything you are shown is that literal string.</p></li>
      <li><h4>It measures each segment by locating it, not by guessing</h4>
        <p>Tokenizing each message separately and adding up is wrong: the template inserts text between messages and
        byte-pair encoding merges across the joins. Instead chatprint renders growing prefixes of the conversation and
        finds where each one stops matching the real prompt. Those cut points tile the token array exactly, so the
        column always sums to the true total.</p></li>
      <li><h4>It finds the tool definitions wherever the template hid them</h4>
        <p>Templates do not agree on where tool schemas go. Qwen splices them into the system block; Llama puts them
        inside your <em>first user message</em>. So chatprint locates the serialised schema in the rendered text and
        carves it out of whichever segment swallowed it — otherwise a two-word "hello" appears to cost two thousand tokens.</p></li>
      <li><h4>Then it packs it to fit</h4>
        <p>Set a budget and the packer drops turns until the prompt fits, without orphaning a tool result from its
        call, without silently dropping your system prompt, and without removing the question you actually asked.</p></li>
    </ol>`,
  );
}

function showPrivacy(): void {
  openModal(
    'Privacy & threat model',
    `
    <h3>Protected</h3>
    <ul>
      <li><strong>Your prompt never leaves the tab.</strong> Messages, tool schemas and retrieved documents are parsed,
      rendered and tokenized in your browser. There is no server component to this tool at all — it is static files on
      GitHub Pages.</li>
      <li><strong>Nothing is stored.</strong> Your input is not written to localStorage, IndexedDB or a cookie. Reload
      the page and it is gone.</li>
      <li><strong>The artefacts are generated locally.</strong> rendered-prompt.txt, token-budget.csv and
      messages.trimmed.json are built in the tab and saved by your browser.</li>
    </ul>

    <h3>Not protected</h3>
    <ul>
      <li><strong>Hugging Face learns which model you selected.</strong> To count tokens exactly, chatprint downloads
      that model's public tokenizer from <code>huggingface.co</code>. That request reveals your IP address and the
      repository id — never your prompt, which is not a parameter to it and never could be. Once cached, it does not
      happen again.</li>
      <li><strong>Counts for Claude are estimates, and are labelled as such.</strong> Anthropic publishes no local
      tokenizer for its current models, so that row is a proxy figure and can be materially low. Their own
      <code>/v1/messages/count_tokens</code> endpoint is free and returns an exact number including tools; chatprint
      does not call it, because calling an API would make this tool a client for a server.</li>
      <li><strong>Counts for the OpenAI encodings use OpenAI's documented per-message accounting.</strong> The
      vocabulary is exact; the framing their API wraps around each message is not published, so those totals are a
      documented approximation rather than a byte-exact render. The open models are exact, because their template is
      a file you can read.</li>
    </ul>

    <h3>Trust surface</h3>
    <ul>
      <li>The static bundle served by GitHub Pages, and the TLS between you and it.</li>
      <li><code>huggingface.co</code> and its CDN, for the tokenizer download described above.</li>
      <li>A Cloudflare Web Analytics beacon records anonymous page views — no cookies, no fingerprinting, no
      cross-site tracking; your files/data are never sent to it.</li>
      <li>Feedback you choose to send is sent to feedback.benrichardson.dev. Nothing is sent unless you open the
      feedback form and press Send; your files and data never are.</li>
      <li>A Content-Security-Policy on this page restricts network access to exactly those hosts. You can read it in
      the page source, and you can watch every request the tool makes in the event log.</li>
    </ul>`,
  );
}

function showAbout(): void {
  openModal(
    'About chatprint',
    `
    <p>chatprint shows you the literal string a language model receives, and where your context window actually went.
    It exists because the two things that eat a context window — tool definitions and retrieved documents — are the two
    things you never see, and because the numbers you can get elsewhere are either exact for the wrong format or the
    right format with no numbers at all.</p>

    <p>The free tools split cleanly in two. Token counters like tiktokenizer count accurately but fake the chat format
    with a hard-coded builder for a handful of OpenAI models. Hugging Face's own chat-template playground renders the
    real Jinja template, tools and all, but cannot count a single token. chatprint joins the two, and adds the parts
    neither has: per-segment attribution, a context-window fit check, a packer, a model-vs-model diff and a
    prompt-cache prefix view.</p>

    <p>Everything runs in the browser. Built by
    <a href="https://benrichardson.dev/" target="_blank" rel="noopener">Ben Richardson</a> —
    more tools and sites at <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">lab.benrichardson.dev</a>.</p>

    <p class="muted">Source: <a href="https://github.com/ben-gy/chatprint" target="_blank" rel="noopener">github.com/ben-gy/chatprint</a>.
    Chat templates and tokenizers are fetched from their publishers' Hugging Face repositories and remain under their
    own licences; chatprint redistributes none of them.</p>

    <p class="muted"><button type="button" class="pill" id="copy-log">${icon('copy')} Copy the event log</button></p>`,
  );
  document.getElementById('copy-log')?.addEventListener('click', async () => {
    if (await copyText(logAsText())) toast('Event log copied.');
  });
}
